import React from 'react';

/**
 * Toggle - A reusable toggle switch component
 * 
 * @param {boolean} checked - Whether the toggle is on or off
 * @param {function} onChange - Callback when toggle state changes
 * @param {string} label - Optional label text to display
 * @param {boolean} disabled - Whether the toggle is disabled
 * @param {string} labelPosition - Position of label: 'left' (default) or 'right'
 * @param {string} size - Size of toggle: 'sm', 'md' (default), or 'lg'
 */
function Toggle({ 
  checked, 
  onChange, 
  label, 
  disabled = false, 
  labelPosition = 'left',
  size = 'md' 
}) {
  // Size configurations
  const sizes = {
    sm: { width: 36, height: 20, knobSize: 14, knobOffset: 3, gap: 8 },
    md: { width: 44, height: 24, knobSize: 18, knobOffset: 3, gap: 12 },
    lg: { width: 52, height: 28, knobSize: 22, knobOffset: 3, gap: 14 }
  };

  const s = sizes[size] || sizes.md;

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent event from bubbling up
    if (!disabled && onChange) {
      onChange(!checked);
    }
  };

  const labelElement = label && (
    <span style={{ 
      fontSize: size === 'sm' ? '0.85rem' : '0.9rem', 
      fontWeight: 500, 
      color: disabled ? 'var(--text-secondary)' : 'var(--text-primary)',
      whiteSpace: 'nowrap',
      opacity: disabled ? 0.6 : 1
    }}>
      {label}
    </span>
  );

  return (
    <div 
      style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: `${s.gap}px`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        opacity: disabled ? 0.6 : 1
      }}
      onClick={handleClick}
      onMouseDown={(e) => e.stopPropagation()}
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          handleClick(e);
        }
      }}
    >
      {labelPosition === 'left' && labelElement}
      
      <div style={{
        width: `${s.width}px`,
        height: `${s.height}px`,
        backgroundColor: checked ? 'var(--primary-color)' : 'var(--border-color)',
        borderRadius: `${s.height / 2}px`,
        position: 'relative',
        transition: 'background-color 0.2s',
        flexShrink: 0
      }}>
        <div style={{
          width: `${s.knobSize}px`,
          height: `${s.knobSize}px`,
          backgroundColor: 'white',
          borderRadius: '50%',
          position: 'absolute',
          top: `${s.knobOffset}px`,
          left: checked ? `${s.width - s.knobSize - s.knobOffset}px` : `${s.knobOffset}px`,
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
        }} />
      </div>

      {labelPosition === 'right' && labelElement}
    </div>
  );
}

export default Toggle;
